import React, { useState, useEffect } from 'react';
import { apiClient } from '../../utils/api';
import { toast } from 'sonner';
import {
  Loader2, Plus, Edit2, Trash2, Eye, EyeOff, Star, Building2, Search
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import ImageUploader from '../../components/admin/ImageUploader';

const PROPERTY_TYPES = [
  { value: 'apartment', label: 'Апартамент' },
  { value: 'house', label: 'Къща' },
  { value: 'land', label: 'Парцел' },
  { value: 'office', label: 'Офис' },
  { value: 'commercial', label: 'Магазин' },
  { value: 'garage', label: 'Гараж' }
];

const TRANSACTION_TYPES = [
  { value: 'sale', label: 'Продажба' },
  { value: 'rent', label: 'Под наем' }
];

const STATUSES = [
  { value: 'available', label: 'Наличен' },
  { value: 'sold', label: 'Продаден' },
  { value: 'rented', label: 'Отдаден' },
  { value: 'reserved', label: 'Резервиран' }
];

const emptyForm = {
  title: '',
  slug: '',
  property_type: 'apartment',
  transaction_type: 'sale',
  status: 'available',
  price: '',
  area: '',
  rooms: '',
  bedrooms: '',
  bathrooms: '',
  floor: '',
  total_floors: '',
  year_built: '',
  city: '',
  neighborhood: '',
  address: '',
  description: '',
  short_description: '',
  featured_image: '',
  gallery_images: [],
  features: '',
  amenities: '',
  seo_title: '',
  seo_description: '',
  featured: false,
  published: false
};

const slugify = (s) =>
  s.toString().toLowerCase().trim()
    .replace(/[\u0400-\u04FF]/g, (c) => {
      const map = {
        'а':'a','б':'b','в':'v','г':'g','д':'d','е':'e','ж':'zh','з':'z','и':'i','й':'y',
        'к':'k','л':'l','м':'m','н':'n','о':'o','п':'p','р':'r','с':'s','т':'t','у':'u',
        'ф':'f','х':'h','ц':'ts','ч':'ch','ш':'sh','щ':'sht','ъ':'a','ь':'','ю':'yu','я':'ya'
      };
      return map[c.toLowerCase()] || '';
    })
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const PropertiesManager = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { fetchProperties(); }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/api/properties/?limit=200');
      setProperties(data);
    } catch (e) {
      toast.error('Грешка при зареждане');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm);
    setIsModalOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setFormData({
      ...emptyForm,
      ...p,
      price: String(p.price ?? ''),
      area: String(p.area ?? ''),
      rooms: p.rooms != null ? String(p.rooms) : '',
      bedrooms: p.bedrooms != null ? String(p.bedrooms) : '',
      bathrooms: p.bathrooms != null ? String(p.bathrooms) : '',
      floor: p.floor != null ? String(p.floor) : '',
      total_floors: p.total_floors != null ? String(p.total_floors) : '',
      year_built: p.year_built != null ? String(p.year_built) : '',
      features: (p.features || []).join(', '),
      amenities: (p.amenities || []).join(', '),
      gallery_images: p.gallery_images || [],
      featured_image: p.featured_image || '',
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        title: formData.title,
        slug: formData.slug || slugify(formData.title),
        property_type: formData.property_type,
        transaction_type: formData.transaction_type,
        status: formData.status,
        price: parseFloat(formData.price) || 0,
        area: parseFloat(formData.area) || 0,
        rooms: formData.rooms ? parseInt(formData.rooms) : null,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        floor: formData.floor ? parseInt(formData.floor) : null,
        total_floors: formData.total_floors ? parseInt(formData.total_floors) : null,
        year_built: formData.year_built ? parseInt(formData.year_built) : null,
        city: formData.city,
        neighborhood: formData.neighborhood || null,
        address: formData.address || null,
        description: formData.description,
        short_description: formData.short_description || null,
        featured_image: formData.featured_image || null,
        gallery_images: formData.gallery_images || [],
        features: formData.features.split(',').map(s => s.trim()).filter(Boolean),
        amenities: formData.amenities.split(',').map(s => s.trim()).filter(Boolean),
        seo_title: formData.seo_title || null,
        seo_description: formData.seo_description || null,
        seo_keywords: [],
        featured: !!formData.featured,
        published: !!formData.published,
        tags: []
      };

      if (editing) {
        await apiClient.put(`/api/properties/${editing._id}`, payload);
        toast.success('Имотът е обновен');
      } else {
        await apiClient.post('/api/properties/', payload);
        toast.success('Имотът е създаден');
      }
      setIsModalOpen(false);
      fetchProperties();
    } catch (err) {
      toast.error(err.message || 'Грешка');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете?')) return;
    try {
      await apiClient.delete(`/api/properties/${id}`);
      toast.success('Изтрит');
      fetchProperties();
    } catch (e) {
      toast.error('Грешка при изтриване');
    }
  };

  const togglePublish = async (p) => {
    try {
      await apiClient.patch(`/api/properties/${p._id}/publish?published=${!p.published}`);
      toast.success(p.published ? 'Скрит' : 'Публикуван');
      fetchProperties();
    } catch { toast.error('Грешка'); }
  };

  const toggleFeatured = async (p) => {
    try {
      await apiClient.patch(`/api/properties/${p._id}/featured?featured=${!p.featured}`);
      fetchProperties();
    } catch { toast.error('Грешка'); }
  };

  const filtered = properties.filter(p => {
    const matchSearch = !search ||
      p.title?.toLowerCase().includes(search.toLowerCase()) ||
      p.city?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || p.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const typeLabel = (v) => PROPERTY_TYPES.find(t => t.value === v)?.label || v;
  const statusLabel = (v) => STATUSES.find(t => t.value === v)?.label || v;

  if (loading && properties.length === 0) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="properties-manager">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-serif font-semibold text-[#F6F2E8]">Имоти</h1>
          <p className="text-[#A9A9A9] mt-1">{properties.length} имот{properties.length === 1 ? '' : 'а'} общо</p>
        </div>
        <Button onClick={openCreate} className="btn-primary" data-testid="add-property-btn">
          <Plus className="w-4 h-4 mr-2" /> Добави имот
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A9A9A9]" />
          <Input
            placeholder="Търсене по заглавие или град..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-[#0B0B0B] border-[#D4AF37]/20"
            data-testid="properties-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 h-10 bg-[#0B0B0B] border border-[#D4AF37]/20 rounded-md text-[#F6F2E8] text-sm"
          data-testid="properties-status-filter"
        >
          <option value="">Всички статуси</option>
          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((p) => (
          <div key={p._id} className="bg-[#111111] border border-[#D4AF37]/10 rounded-xl overflow-hidden hover:border-[#D4AF37]/30 transition-all" data-testid={`property-card-${p._id}`}>
            <div className="h-48 bg-gradient-to-br from-[#D4AF37]/20 to-[#B8892E]/20 flex items-center justify-center relative overflow-hidden">
              {p.featured_image ? (
                <img
                  src={p.featured_image.startsWith('http') ? p.featured_image : `${process.env.REACT_APP_BACKEND_URL}${p.featured_image}`}
                  alt={p.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : null}
              {!p.featured_image && (
                <Building2 className="w-16 h-16 text-[#D4AF37]/40" />
              )}
              {!p.published && (
                <span className="absolute top-2 left-2 px-2 py-1 bg-yellow-500/90 text-black text-xs font-medium rounded">Чернова</span>
              )}
              {p.featured && (
                <Star className="absolute top-2 right-2 w-5 h-5 text-[#D4AF37] fill-[#D4AF37]" />
              )}
            </div>

            <div className="p-4 space-y-3">
              <div>
                <h3 className="font-serif font-semibold text-[#F6F2E8] truncate">{p.title}</h3>
                <p className="text-sm text-[#A9A9A9]">{p.city}{p.neighborhood ? `, ${p.neighborhood}` : ''}</p>
              </div>

              <div className="flex items-center justify-between text-xs">
                <span className="px-2 py-1 bg-[#0B0B0B] rounded text-[#A9A9A9]">{typeLabel(p.property_type)}</span>
                <span className="text-[#A9A9A9]">{p.area} м²</span>
                <span className="px-2 py-1 bg-[#0B0B0B] rounded text-[#D4AF37]">{statusLabel(p.status)}</span>
              </div>

              <p className="text-xl font-bold text-[#D4AF37]">{(p.price || 0).toLocaleString()} €</p>

              <div className="flex items-center gap-2 pt-3 border-t border-[#D4AF37]/10">
                <button onClick={() => togglePublish(p)} className="flex-1 flex items-center justify-center gap-1 px-2 py-2 text-xs bg-[#0B0B0B] rounded hover:bg-[#D4AF37]/10" data-testid={`publish-btn-${p._id}`}>
                  {p.published ? <EyeOff size={14} /> : <Eye size={14} />}
                  <span>{p.published ? 'Скрий' : 'Публ.'}</span>
                </button>
                <button onClick={() => toggleFeatured(p)} className="p-2 bg-[#0B0B0B] rounded hover:bg-[#D4AF37]/10" title="Featured">
                  <Star size={14} className={p.featured ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-[#A9A9A9]'} />
                </button>
                <button onClick={() => openEdit(p)} className="p-2 bg-[#0B0B0B] rounded hover:bg-[#D4AF37]/10" data-testid={`edit-btn-${p._id}`}>
                  <Edit2 size={14} className="text-[#D4AF37]" />
                </button>
                <button onClick={() => handleDelete(p._id)} className="p-2 bg-[#0B0B0B] rounded hover:bg-red-500/10" data-testid={`delete-btn-${p._id}`}>
                  <Trash2 size={14} className="text-red-400" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#A9A9A9]">
            Няма намерени имоти. Натиснете "Добави имот" за първото обявление.
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#111111] border-[#D4AF37]/20 text-[#F6F2E8] max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="property-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif">
              {editing ? 'Редактирай имот' : 'Нов имот'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            {/* Featured image */}
            <div>
              <Label className="block mb-2">Главно изображение</Label>
              <ImageUploader
                value={formData.featured_image}
                onChange={(url) => setFormData({...formData, featured_image: url})}
                label="Главно"
              />
            </div>

            {/* Gallery */}
            <div>
              <Label className="block mb-2">Галерия (множество)</Label>
              <ImageUploader
                multiple
                value={formData.gallery_images}
                onChange={(urls) => setFormData({...formData, gallery_images: urls})}
                label="Добави"
              />
            </div>

            {/* Basic */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Заглавие *</Label>
                <Input
                  required
                  value={formData.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    setFormData({...formData, title, slug: editing ? formData.slug : slugify(title)});
                  }}
                  className="bg-[#0B0B0B] border-[#D4AF37]/20"
                  data-testid="property-title-input"
                />
              </div>

              <div className="col-span-2">
                <Label>Slug</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  className="bg-[#0B0B0B] border-[#D4AF37]/20"
                  placeholder="auto-generated"
                  data-testid="property-slug-input"
                />
              </div>

              <div>
                <Label>Тип имот *</Label>
                <select
                  required
                  value={formData.property_type}
                  onChange={(e) => setFormData({...formData, property_type: e.target.value})}
                  className="w-full h-10 px-3 rounded-md bg-[#0B0B0B] border border-[#D4AF37]/20 text-[#F6F2E8]"
                  data-testid="property-type-select"
                >
                  {PROPERTY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              <div>
                <Label>Сделка *</Label>
                <select
                  required
                  value={formData.transaction_type}
                  onChange={(e) => setFormData({...formData, transaction_type: e.target.value})}
                  className="w-full h-10 px-3 rounded-md bg-[#0B0B0B] border border-[#D4AF37]/20 text-[#F6F2E8]"
                >
                  {TRANSACTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              <div>
                <Label>Статус</Label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full h-10 px-3 rounded-md bg-[#0B0B0B] border border-[#D4AF37]/20 text-[#F6F2E8]"
                >
                  {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>

              <div>
                <Label>Цена (€) *</Label>
                <Input required type="number" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20" data-testid="property-price-input" />
              </div>

              <div>
                <Label>Площ (м²) *</Label>
                <Input required type="number" value={formData.area} onChange={(e) => setFormData({...formData, area: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20" />
              </div>

              <div>
                <Label>Стаи</Label>
                <Input type="number" value={formData.rooms} onChange={(e) => setFormData({...formData, rooms: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20" />
              </div>

              <div>
                <Label>Спални</Label>
                <Input type="number" value={formData.bedrooms} onChange={(e) => setFormData({...formData, bedrooms: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20" />
              </div>

              <div>
                <Label>Бани</Label>
                <Input type="number" value={formData.bathrooms} onChange={(e) => setFormData({...formData, bathrooms: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20" />
              </div>

              <div>
                <Label>Етаж</Label>
                <Input type="number" value={formData.floor} onChange={(e) => setFormData({...formData, floor: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20" />
              </div>

              <div>
                <Label>Общо етажи</Label>
                <Input type="number" value={formData.total_floors} onChange={(e) => setFormData({...formData, total_floors: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20" />
              </div>

              <div>
                <Label>Година на строеж</Label>
                <Input type="number" value={formData.year_built} onChange={(e) => setFormData({...formData, year_built: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20" />
              </div>

              <div>
                <Label>Град *</Label>
                <Input required value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20" placeholder="София" data-testid="property-city-input" />
              </div>

              <div>
                <Label>Квартал</Label>
                <Input value={formData.neighborhood} onChange={(e) => setFormData({...formData, neighborhood: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20" placeholder="Лозенец" />
              </div>

              <div className="col-span-2">
                <Label>Адрес</Label>
                <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20" />
              </div>

              <div className="col-span-2">
                <Label>Кратко описание</Label>
                <Textarea rows={2} value={formData.short_description} onChange={(e) => setFormData({...formData, short_description: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20 resize-none" />
              </div>

              <div className="col-span-2">
                <Label>Описание *</Label>
                <Textarea required rows={6} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20 resize-none" data-testid="property-description-input" />
                <p className="text-xs text-[#A9A9A9] mt-1">Поддържа HTML.</p>
              </div>

              <div className="col-span-2">
                <Label>Характеристики (разделени със запетая)</Label>
                <Input value={formData.features} onChange={(e) => setFormData({...formData, features: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20" placeholder="паркинг, асансьор, балкон" />
              </div>

              <div className="col-span-2">
                <Label>Удобства (разделени със запетая)</Label>
                <Input value={formData.amenities} onChange={(e) => setFormData({...formData, amenities: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20" placeholder="климатик, парно, охрана" />
              </div>

              <div className="col-span-2 pt-2 border-t border-[#D4AF37]/10">
                <p className="text-sm font-medium mb-2 text-[#D4AF37]">SEO</p>
              </div>

              <div className="col-span-2">
                <Label>SEO заглавие</Label>
                <Input value={formData.seo_title} onChange={(e) => setFormData({...formData, seo_title: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20" />
              </div>

              <div className="col-span-2">
                <Label>SEO описание</Label>
                <Textarea rows={2} value={formData.seo_description} onChange={(e) => setFormData({...formData, seo_description: e.target.value})} className="bg-[#0B0B0B] border-[#D4AF37]/20 resize-none" />
              </div>

              <div className="col-span-2 flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.published} onChange={(e) => setFormData({...formData, published: e.target.checked})} className="w-4 h-4" data-testid="property-published-checkbox" />
                  <span className="text-sm">Публикуван</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.featured} onChange={(e) => setFormData({...formData, featured: e.target.checked})} className="w-4 h-4" />
                  <span className="text-sm">Препоръчан (Featured)</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-[#D4AF37]/10">
              <Button type="button" onClick={() => setIsModalOpen(false)} className="btn-secondary">Отказ</Button>
              <Button type="submit" disabled={submitting} className="btn-primary" data-testid="property-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Обнови' : 'Създай')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertiesManager;
